import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, LayoutGrid, List, Loader2, SlidersHorizontal, Sparkles, X, SearchX, ScanSearch } from 'lucide-react';
import SearchBar from '../components/explore/SearchBar';
import FilterPanel from '../components/explore/FilterPanel';
import ItemCard, { ItemCardSkeleton } from '../components/items/ItemCard';
import ItemTable from '../components/items/ItemTable';
import ItemDetailSheet from '../components/items/ItemDetailSheet';
import { EmptyState, ErrorState } from '../components/common/States';
import WaitingModal from '../components/common/WaitingModal';
import { startTour, startTourOnce } from '../lib/tour';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetTitle } from '../components/ui/sheet';
import { Hint } from '../components/ui/tooltip';
import { useCapabilities, useMeta } from '../hooks/queries';
import { api, errorMessage, toSearchParams } from '../lib/api';
import {
  FILTER_KEYS, QUICK_VIEWS, SORT_OPTIONS, countFilters, filterChips, normaliseFilters, parseFilters,
  quickViewActive, removeChip, toggleQuickView,
} from '../lib/filters';
import { fmtEur, fmtInt } from '../lib/format';
import { cn } from '../lib/utils';

const PAGE_SIZE = 48;

/** API params for GET /api/items/ derived from the URL (single source of truth). */
function itemParams(sp) {
  const q = sp.get('q') || '';
  const sq = sp.get('sq') || '';
  const p = { ...parseFilters(sp), page_size: PAGE_SIZE };
  if (q) {
    p.q = q;
    p.mode = 'hybrid';
  }
  // Ask AI: the visual description only feeds image similarity
  if (sq) p.sq = sq;
  const sort = sp.get('sort');
  if (sort) p.sort = sort;
  return p;
}
const itemsKey = (params) => ['items', toSearchParams(params).toString()];

export default function Explore() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const itemMatch = useMatch('/item/:odooId');
  const selectedId = itemMatch?.params.odooId || null;
  const qc = useQueryClient();
  const caps = useCapabilities();
  const { data: meta } = useMeta();
  const inputRef = useRef(null);
  const [mobileFilters, setMobileFilters] = useState(false);

  const tab = ['search', 'ai', 'visual'].includes(sp.get('tab')) ? sp.get('tab') : 'search';
  const view = sp.get('view') === 'table' ? 'table' : 'grid';
  const q = sp.get('q') || '';
  const sq = sp.get('sq') || '';
  const filters = useMemo(() => parseFilters(sp), [sp]);
  const params = useMemo(() => itemParams(sp), [sp]);
  const sort = sp.get('sort') || (q || sq ? 'relevance' : 'value');
  const likeId = sp.get('like');

  // ---- URL helpers ---------------------------------------------------------
  const update = useCallback(
    (mutate, { replace = false } = {}) => {
      const next = new URLSearchParams(sp);
      mutate(next);
      setSp(next, { replace });
    },
    [sp, setSp]
  );
  const setFilters = useCallback(
    (f) =>
      update((next) => {
        FILTER_KEYS.forEach((k) => next.delete(k));
        toSearchParams(f).forEach((v, k) => next.append(k, v));
      }),
    [update]
  );
  const setParam = (key, value) => update((next) => (value ? next.set(key, value) : next.delete(key)));

  // ---- Visual state (file is local; "like" item is in the URL) --------------
  const [visualFile, setVisualFile] = useState(null);
  const [visualPreview, setVisualPreview] = useState(null);
  useEffect(() => {
    if (!visualFile) return setVisualPreview(null);
    const url = URL.createObjectURL(visualFile);
    setVisualPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [visualFile]);
  const likeItem = useQuery({ queryKey: ['item', String(likeId)], queryFn: () => api.item(likeId), enabled: tab === 'visual' && !!likeId });

  // ---- AI state -------------------------------------------------------------
  const [aiInfo, setAiInfo] = useState(null); // {query, explanation, keywords, relaxed}
  const aiQuestion = sp.get('aiq') || '';
  const aiSearch = useMutation({
    mutationFn: (query) => api.aiSearch(query),
    onSuccess: (res, query) => {
      const interp = res.interpretation || {};
      setAiInfo({ query, explanation: interp.explanation, keywords: interp.keywords || [], relaxed: res.relaxed || [] });
      const next = new URLSearchParams();
      next.set('tab', 'ai');
      next.set('aiq', query);
      if (view === 'table') next.set('view', 'table');
      if (interp.keywords?.length) next.set('q', interp.keywords.join(' '));
      if (interp.semantic_query) next.set('sq', interp.semantic_query);
      if (interp.sort && interp.sort !== 'relevance') next.set('sort', interp.sort);
      toSearchParams(normaliseFilters(interp.filters)).forEach((v, k) => next.append(k, v));
      // Seed the GET cache with the AI response so the first page is not fetched twice.
      const firstPage = { page: 1, page_size: PAGE_SIZE, ...res };
      delete firstPage.interpretation;
      delete firstPage.relaxed;
      qc.setQueryData(itemsKey(itemParams(next)), { pages: [firstPage], pageParams: [1] });
      setSp(next);
    },
  });

  // ---- Data -----------------------------------------------------------------
  const listing = useInfiniteQuery({
    queryKey: itemsKey(params),
    queryFn: ({ pageParam }) => api.items({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last) => {
      const page = Number(last.page || 1);
      const size = Number(last.page_size || PAGE_SIZE);
      return page * size < Number(last.count || 0) ? page + 1 : undefined;
    },
    placeholderData: keepPreviousData,
    enabled: tab !== 'visual',
  });

  const visualParams = useMemo(() => ({ ...filters, ...(sp.get('sort') ? { sort: sp.get('sort') } : {}) }), [filters, sp]);
  const visualQuery = useQuery({
    queryKey: ['visual', visualFile ? `${visualFile.name}:${visualFile.size}:${visualFile.lastModified}` : null, toSearchParams(visualParams).toString()],
    queryFn: () => api.visualSearch(visualFile, visualParams),
    enabled: tab === 'visual' && !!visualFile,
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  });
  const likeQuery = useQuery({
    queryKey: ['similar', String(likeId), 'looks'],
    queryFn: () => api.similar(likeId, 'looks'),
    enabled: tab === 'visual' && !visualFile && !!likeId,
  });

  // Normalise whichever source is active into one shape
  let source = 'list';
  let results = [];
  let first = null;
  let isLoading = false;
  let isFetching = false;
  let error = null;
  if (tab === 'visual') {
    if (visualFile) {
      source = 'visual';
      first = visualQuery.data || null;
      results = first?.results || [];
      isLoading = visualQuery.isLoading;
      isFetching = visualQuery.isFetching;
      error = visualQuery.error;
    } else if (likeId) {
      source = 'like';
      results = likeQuery.data?.results || [];
      first = likeQuery.data ? { count: results.length } : null;
      isLoading = likeQuery.isLoading;
      isFetching = likeQuery.isFetching;
      error = likeQuery.error;
    } else {
      source = 'visual-empty';
    }
  } else {
    first = listing.data?.pages?.[0] || null;
    results = listing.data?.pages?.flatMap((p) => p.results || []) || [];
    isLoading = listing.isLoading;
    isFetching = listing.isFetching && !listing.isFetchingNextPage;
    error = listing.error;
  }
  const facets = source === 'list' || source === 'visual' ? first?.facets : null;
  const [lastFacets, setLastFacets] = useState(null);
  useEffect(() => {
    if (facets) setLastFacets(facets);
  }, [facets]);

  // ---- Keyboard: "/" focuses the search input ---------------------------------
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))) return;
      e.preventDefault();
      if (tab === 'visual') update((n) => n.set('tab', 'search'));
      setTimeout(() => inputRef.current?.focus(), 0);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tab, update]);

  // ---- Actions ----------------------------------------------------------------
  const openItem = (item) => {
    const id = typeof item === 'object' ? item.odoo_product_id : item;
    navigate({ pathname: `/item/${id}`, search: location.search }, { state: { fromExplore: true } });
  };
  const closeItem = () => navigate({ pathname: '/explore', search: location.search });
  const findSimilarLook = (item) => {
    setVisualFile(null);
    const next = new URLSearchParams(sp);
    next.set('tab', 'visual');
    next.set('like', String(item.odoo_product_id));
    navigate({ pathname: '/explore', search: `?${next.toString()}` });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const changeTab = (t) =>
    update((next) => {
      if (t === 'search') next.delete('tab');
      else next.set('tab', t);
    });

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const exportCsv = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const p = { ...params };
      delete p.page_size;
      await api.exportItems(p);
    } catch (e) {
      setExportError(e);
    } finally {
      setExporting(false);
    }
  };

  const continueTour = (next) => navigate(`/${next}?tour=1`);
  // guided tour: once on the first visit (when products are on screen), or on request (?tour=1)
  const hasResults = results.length > 0;
  useEffect(() => {
    if (!hasResults) return undefined;
    if (sp.get('tour') === '1') {
      // start first, then drop ?tour=1: removing it earlier re-runs this effect and cancels the timer
      const t = setTimeout(() => {
        startTour('explore', { onContinue: continueTour });
        update((n) => n.delete('tour'), { replace: true });
      }, 400);
      return () => clearTimeout(t);
    }
    return startTourOnce('explore', 700, { onContinue: continueTour });
  }, [hasResults, sp.get('tour')]); // eslint-disable-line react-hooks/exhaustive-deps

  // friendly overlay when a search the user started is slow (filter clicks are not)
  const waitingMode = aiSearch.isPending
    ? 'ai'
    : tab === 'visual' && visualFile && visualQuery.isFetching
      ? 'visual'
      : tab !== 'visual' && (q || sq) && listing.isFetching && !listing.isFetchingNextPage
        ? 'search'
        : null;

  const chips = filterChips(filters);
  const nFilters = countFilters(filters);
  const showAiBanner = tab === 'ai' && (aiInfo?.query === aiQuestion ? aiInfo : aiQuestion);

  const filterPanel = (
    <FilterPanel
      facets={facets || lastFacets}
      filters={filters}
      onChange={setFilters}
      vocab={meta?.vocab}
      definitions={meta?.definitions}
      disabledNote={source === 'like' ? 'Filters do not apply to "similar look" results.' : null}
    />
  );

  return (
    <div className="w-full px-3 pb-16 pt-4 sm:px-6 lg:pt-6">
      {/* Search hero */}
      <div className="mb-5">
        <SearchBar
          tab={tab}
          onTabChange={changeTab}
          caps={caps}
          inputRef={inputRef}
          search={{ value: q, onSubmit: (text) => update((n) => { n.delete('sq'); if (text) n.set('q', text); else n.delete('q'); }) }}
          ai={{ value: aiQuestion, onSubmit: (text) => aiSearch.mutate(text), loading: aiSearch.isPending }}
          visual={{
            preview: visualPreview || likeItem.data?.item?.image_url || (likeId ? '' : null),
            sourceLabel: visualFile ? visualFile.name : likeId ? `Similar look to: ${likeItem.data?.item?.title || `#${likeId}`}` : null,
            onFile: (f) => {
              setVisualFile(f);
              if (likeId) update((n) => n.delete('like'), { replace: true });
            },
            onClear: () => {
              setVisualFile(null);
              update((n) => n.delete('like'));
            },
            loading: isFetching,
          }}
        />
        {aiSearch.error && <ErrorState className="mt-3" compact error={aiSearch.error} title="AI search failed" />}
        {showAiBanner && (
          <div className="mt-3 rounded-xl border border-violet-200 bg-violet-50/60 p-3 text-sm dark:border-violet-500/30 dark:bg-violet-500/10">
            <div className="flex items-start gap-2">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-violet-600 dark:text-violet-300" />
              <div className="min-w-0 flex-1 space-y-2">
                <p className="leading-relaxed">
                  {aiInfo?.query === aiQuestion && aiInfo.explanation ? aiInfo.explanation : <>Results for “{aiQuestion}”.</>}
                </p>
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">Understood as:</span>
                  {q && <Chip label={`“${q}”`} onRemove={() => update((n) => n.delete('q'))} accent />}
                  {sq && <Chip label={`looks like: ${sq}`} onRemove={() => update((n) => n.delete('sq'))} accent />}
                  {chips.map((c) => (
                    <Chip key={`${c.key}:${c.value}`} label={c.label} onRemove={() => setFilters(removeChip(filters, c))} accent />
                  ))}
                  {!q && !sq && !chips.length && <span className="text-xs text-muted-foreground">no constraints left — showing everything</span>}
                </div>
                {aiInfo?.query === aiQuestion && aiInfo.relaxed?.length > 0 && (
                  <p className="text-xs text-violet-900/80 dark:text-violet-200/80">
                    Nothing matched every condition, so these limits were dropped: {aiInfo.relaxed.map(relaxedLabel).join(', ')}.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-6">
        {/* Desktop filters */}
        <aside data-tour="filters" className="sticky top-4 hidden max-h-[calc(100vh-2rem)] w-60 shrink-0 overflow-y-auto pr-1 lg:block">{filterPanel}</aside>

        <section className="min-w-0 flex-1 space-y-3">
          {/* Header strip */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-tour="results-header">
            <Button variant="outline" size="sm" className="h-8 gap-1.5 lg:hidden" onClick={() => setMobileFilters(true)}>
              <SlidersHorizontal className="h-3.5 w-3.5" /> Filters{nFilters ? ` (${nFilters})` : ''}
            </Button>
            <div className="flex items-baseline gap-x-4 gap-y-1 text-sm tabular-nums">
              {first ? (
                <>
                  <span>
                    <span className="font-semibold">{fmtInt(first.count)}</span> <span className="text-muted-foreground">{Number(first.count) === 1 ? 'item' : 'items'}</span>
                  </span>
                  {first.total_value != null && (
                    <span>
                      <span className="font-semibold">{fmtEur(first.total_value)}</span> <span className="text-muted-foreground">value</span>
                    </span>
                  )}
                  {first.total_units != null && (
                    <span>
                      <span className="font-semibold">{fmtInt(first.total_units)}</span> <span className="text-muted-foreground">units</span>
                    </span>
                  )}
                </>
              ) : (
                isLoading && <span className="h-4 w-48 animate-pulse rounded bg-muted" />
              )}
              {isFetching && !isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin self-center text-muted-foreground" />}
              {source === 'list' && q && first?.semantic_used === false && caps.semantic && (
                <span className="text-xs text-muted-foreground">keyword match only</span>
              )}
            </div>

            <div className="ml-auto flex items-center gap-2">
              <Select value={sort} onValueChange={(v) => setParam('sort', v)}>
                <SelectTrigger className="h-8 w-[200px] text-xs" aria-label="Sort">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} className="text-xs">
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex rounded-md border p-0.5">
                <Hint text="Grid">
                  <button
                    type="button"
                    onClick={() => setParam('view', null)}
                    className={cn('flex h-7 w-7 items-center justify-center rounded', view === 'grid' ? 'bg-accent text-foreground' : 'text-muted-foreground')}
                    aria-label="Grid view"
                  >
                    <LayoutGrid className="h-3.5 w-3.5" />
                  </button>
                </Hint>
                <Hint text="Table">
                  <button
                    type="button"
                    onClick={() => setParam('view', 'table')}
                    className={cn('flex h-7 w-7 items-center justify-center rounded', view === 'table' ? 'bg-accent text-foreground' : 'text-muted-foreground')}
                    aria-label="Table view"
                  >
                    <List className="h-3.5 w-3.5" />
                  </button>
                </Hint>
              </div>
              <Hint text={tab === 'visual' ? 'Export works on search / filter results, not on image results' : 'Download every matching row as CSV'}>
                <span data-tour="export">
                  <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={exportCsv} disabled={exporting || tab === 'visual'}>
                    {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                    <span className="hidden sm:inline">Export CSV</span>
                  </Button>
                </span>
              </Hint>
            </div>
          </div>
          {exportError && <ErrorState compact error={exportError} title="Export failed" />}

          {/* Quick views */}
          {source !== 'like' && (
            <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-0.5" data-tour="quick-views">
              {QUICK_VIEWS.map((v) => {
                const active = quickViewActive(filters, v);
                return (
                  <Hint key={v.id} text={v.hint}>
                    <button
                      type="button"
                      onClick={() => setFilters(toggleQuickView(filters, v))}
                      className={cn(
                        'shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition',
                        active ? 'border-foreground bg-foreground text-background' : 'bg-background text-muted-foreground hover:border-foreground/30 hover:text-foreground'
                      )}
                    >
                      {v.label}
                    </button>
                  </Hint>
                );
              })}
            </div>
          )}

          {/* Active filter chips (AI tab shows them in its banner) */}
          {tab !== 'ai' && source !== 'like' && (chips.length > 0 || q || sq) && (
            <div className="flex flex-wrap items-center gap-1.5">
              {q && tab === 'search' && <Chip label={`“${q}”`} onRemove={() => update((n) => n.delete('q'))} />}
              {chips.map((c) => (
                <Chip key={`${c.key}:${c.value}`} label={c.label} onRemove={() => setFilters(removeChip(filters, c))} />
              ))}
              {sq && <Chip label={`looks like: ${sq}`} onRemove={() => update((n) => n.delete('sq'))} />}
              {(chips.length > 1 || (!!q && chips.length > 0)) && (
                <button
                  type="button"
                  onClick={() =>
                    update((n) => {
                      FILTER_KEYS.forEach((k) => n.delete(k));
                      n.delete('q');
                      n.delete('sq');
                    })
                  }
                  className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                >
                  Clear all
                </button>
              )}
            </div>
          )}

          {/* Results */}
          {error && <ErrorState error={error} title={errorTitle(source)} onRetry={() => (source === 'list' ? listing.refetch() : source === 'visual' ? visualQuery.refetch() : likeQuery.refetch())} />}

          {source === 'visual-empty' && (
            <EmptyState icon={ScanSearch} title="Search by image">
              Drop a photo above to find problem stock that looks like it — or use “Find similar look” on any product.
            </EmptyState>
          )}

          {isLoading && !results.length && source !== 'visual-empty' && (
            <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 min-[2000px]:grid-cols-5 min-[2400px]:grid-cols-6">
              {Array.from({ length: 10 }).map((_, i) => (
                <ItemCardSkeleton key={i} />
              ))}
            </div>
          )}

          {!isLoading && !error && first && !results.length && (
            <EmptyState
              icon={SearchX}
              title="No problem stock matches"
              action={
                (nFilters > 0 || q || sq) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      update((n) => {
                        FILTER_KEYS.forEach((k) => n.delete(k));
                        n.delete('q');
                        n.delete('sq');
                      })
                    }
                  >
                    <X className="mr-1.5 h-3.5 w-3.5" /> Clear search and filters
                  </Button>
                )
              }
            >
              {nFilters > 0 || q ? 'Try removing a filter or broadening the search.' : 'Nothing to show here.'}
            </EmptyState>
          )}

          {results.length > 0 && (
            <div className={cn('transition-opacity', isFetching && 'opacity-60')}>
              {view === 'table' ? (
                <ItemTable items={results} sort={sort} onSort={(s) => setParam('sort', s)} onOpen={openItem} onFindSimilar={findSimilarLook} />
              ) : (
                <div className="grid grid-cols-1 gap-3 min-[480px]:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 min-[2000px]:grid-cols-5 min-[2400px]:grid-cols-6">
                  {results.map((it, i) =>
                    i === 0 ? (
                      <div key={it.odoo_product_id} data-tour="card" className="grid">
                        <ItemCard item={it} onOpen={openItem} onFindSimilar={findSimilarLook} />
                      </div>
                    ) : (
                      <ItemCard key={it.odoo_product_id} item={it} onOpen={openItem} onFindSimilar={findSimilarLook} />
                    )
                  )}
                </div>
              )}
            </div>
          )}

          {source === 'list' && listing.hasNextPage && (
            <div className="flex flex-col items-center gap-1 pt-4">
              <Button variant="outline" onClick={() => listing.fetchNextPage()} disabled={listing.isFetchingNextPage} className="min-w-[200px]">
                {listing.isFetchingNextPage ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Load more
              </Button>
              <span className="text-xs tabular-nums text-muted-foreground">
                Showing {fmtInt(results.length)} of {fmtInt(first?.count)}
              </span>
            </div>
          )}
          {listing.isFetchNextPageError && <p className="text-center text-xs text-destructive">{errorMessage(listing.error)}</p>}
        </section>
      </div>

      {/* Mobile filters */}
      <Sheet open={mobileFilters} onOpenChange={setMobileFilters}>
        <SheetContent side="left" className="overflow-y-auto p-4">
          <SheetTitle className="sr-only">Filters</SheetTitle>
          {filterPanel}
          <Button className="mt-4 w-full" onClick={() => setMobileFilters(false)}>
            Show {first ? fmtInt(first.count) : ''} results
          </Button>
        </SheetContent>
      </Sheet>

      <ItemDetailSheet odooId={selectedId} onClose={closeItem} onOpenItem={(id) => openItem(id)} />
      <WaitingModal active={!!waitingMode} mode={waitingMode || 'search'} />
    </div>
  );
}

const RELAXED_LABELS = {
  max_price: 'max price', min_price: 'min price', max_unit_cost: 'max cost', min_unit_cost: 'min cost',
  min_qty: 'minimum pieces', color: 'colour', material: 'material', min_completeness: 'listing quality',
  max_completeness: 'listing quality', min_age: 'age',
};
const relaxedLabel = (k) => RELAXED_LABELS[k] || k;

function errorTitle(source) {
  if (source === 'visual') return 'Visual search failed';
  if (source === 'like') return 'Could not find similar items';
  return 'Search failed';
}

function Chip({ label, onRemove, accent }) {
  return (
    <span
      className={cn(
        'inline-flex max-w-full items-center gap-1 rounded-full border py-0.5 pl-2.5 pr-1 text-xs',
        accent ? 'border-violet-200 bg-background text-violet-900 dark:border-violet-500/30 dark:text-violet-200' : 'bg-muted/50'
      )}
    >
      <span className="truncate">{label}</span>
      <button type="button" onClick={onRemove} className="flex h-4 w-4 items-center justify-center rounded-full hover:bg-foreground/10" aria-label={`Remove ${label}`}>
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}
